import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSAlert, IDSButton, IDSCard } from "@inera/ids-react";
import { useAuth } from "../contexts/AuthContext";
import { useOrganisation } from "../contexts/OrganisationContext";
import { formatRoleName } from "../utils/tokenUtils";
import { fetchContractsForOrg } from "../services/contractsAPI";
import {
  fetchOrganisationTestExecutionSummaries,
  fetchTestExecutionSummaries,
  fetchTestExecutions,
} from "../services/testExecutionAPI";
import { fetchReviewerDecisionQueue } from "../services/reviewerDecisionQueueAPI";
import { getMySubmissions, getReviewerSubmissions } from "../services/E2EAPI";
import { partitionReviewerReports } from "../utils/reviewerReportTabs";
import {
  partitionMySubmissions,
  partitionReviewerSubmissions,
} from "../utils/e2eSubmissionPolicy";
import { fetchOrganisations } from "../services/organisationsAdminAPI";
import { fetchAllUsers } from "../services/usersAdminAPI";

/** Test execution statuses where the tester still owns the work in the test client. */
const TESTER_ONGOING_STATUSES = new Set(["DRAFT", "PENDING", "RUNNING"]);

/** Statuses that hand a submitted report back to the tester. */
const TESTER_ACTION_STATUSES = new Set(["IN_CLARIFY", "REJECTED"]);

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "1.5rem",
};

const statGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "1rem",
};

const StatCard = ({ value, label, highlight }) => (
  <IDSCard>
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <span
        className="ids-heading-l"
        style={{
          margin: 0,
          color: highlight && value > 0 ? "var(--ids-color-text-error, #b30000)" : undefined,
        }}
      >
        {value}
      </span>
      <span className="ids-body" style={{ margin: 0 }}>
        {label}
      </span>
    </div>
  </IDSCard>
);

const ActionCard = ({ title, description, actionLabel, onClick }) => (
  <IDSCard>
    <div
      style={{ display: "flex", flexDirection: "column", gap: "0.75rem", height: "100%" }}
    >
      <h3 className="ids-heading-s" style={{ margin: 0 }}>
        {title}
      </h3>
      <p className="ids-body" style={{ margin: 0, flex: "1 1 auto" }}>
        {description}
      </p>
      <IDSButton size="s" onClick={onClick} style={{ alignSelf: "flex-start" }}>
        {actionLabel}
      </IDSButton>
    </div>
  </IDSCard>
);

/**
 * Landing page. What it shows is decided by the role picked in the organisation
 * selector, so each role opens on the queue it actually works in rather than a
 * generic description of the whole tool.
 */
function Start() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { activeOrgId, activeRoleId, organisations, openSelector } = useOrganisation();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  const role = activeRoleId?.toLowerCase() || "";
  const isAdmin = activeRoleId === "inera-administrator";
  const isReviewer = activeRoleId === "inera-reviewer";
  const isE2ETester = role.endsWith("-e2e-tester");
  const isE2EReviewer = role.endsWith("-e2e-reviewer");
  // Same "deny by default" shape as ProtectedTesterRoute: no role selected is not a tester.
  const isTester =
    !!activeRoleId && !isAdmin && !isReviewer && !isE2ETester && !isE2EReviewer;

  const activeOrg = organisations.find((org) => org.id === activeOrgId);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !activeRoleId || !activeOrgId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        let next = null;

        if (isTester) {
          const [contracts, myExecutions, orgReports] = await Promise.all([
            fetchContractsForOrg(activeOrgId),
            fetchTestExecutions(activeOrgId),
            fetchOrganisationTestExecutionSummaries(activeOrgId),
          ]);
          next = {
            contracts: contracts.length,
            ongoing: myExecutions.filter((e) => TESTER_ONGOING_STATUSES.has(e.status)).length,
            orgReports: orgReports.length,
            needsAction: orgReports.filter((r) => TESTER_ACTION_STATUSES.has(r.status)).length,
          };
        } else if (isReviewer) {
          const [summaries, decisionQueue] = await Promise.all([
            fetchTestExecutionSummaries(activeOrgId),
            fetchReviewerDecisionQueue(false),
          ]);
          const tabs = partitionReviewerReports(summaries, user?.id);
          next = {
            awaiting: tabs.AWAITING.length,
            mine: tabs.MINE.length,
            decisions: decisionQueue.length,
          };
        } else if (isE2ETester) {
          const { activeSubmissions } = partitionMySubmissions(await getMySubmissions());
          next = {
            active: activeSubmissions.length,
            drafts: activeSubmissions.filter((s) => s.status === "DRAFT").length,
            needsClarification: activeSubmissions.filter((s) => s.status === "IN_CLARIFY").length,
          };
        } else if (isE2EReviewer) {
          const { waitingSubmissions, assignedToMeSubmissions, finishedSubmissions } =
            partitionReviewerSubmissions(await getReviewerSubmissions(), user?.id);
          next = {
            waiting: waitingSubmissions.length,
            assignedToMe: assignedToMeSubmissions.length,
            finished: finishedSubmissions.length,
          };
        } else if (isAdmin) {
          const [orgs, users] = await Promise.all([fetchOrganisations(), fetchAllUsers()]);
          next = { organisations: orgs.length, users: users.length };
        }

        if (!cancelled) {
          setStats(next);
        }
      } catch (err) {
        console.error("Kunde inte hämta översikten för startsidan:", err);
        if (!cancelled) {
          setStatsError("Kunde inte hämta din översikt just nu.");
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    isAuthenticated,
    activeOrgId,
    activeRoleId,
    user?.id,
    isTester,
    isReviewer,
    isE2ETester,
    isE2EReviewer,
    isAdmin,
  ]);

  const greeting = user?.firstName
    ? `Välkommen, ${user.firstName}`
    : "Välkommen till Testverktyget för Tjänstekontrakt";

  const header = (
    <>
      <h1 className="ids-heading-l">{greeting}</h1>
      {activeOrgId && activeRoleId && (
        <p className="ids-body ids-mb-4">
          Du arbetar som <strong>{formatRoleName(activeRoleId)}</strong> i{" "}
          <strong>{activeOrg?.displayName ?? activeOrg?.name ?? "din organisation"}</strong>.{" "}
          <button
            type="button"
            onClick={openSelector}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--ids-color-text-link, #0a5c73)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Byt organisation eller roll
          </button>
        </p>
      )}
    </>
  );

  if (authLoading) {
    return (
      <div className="ids-mt-6">
        <main className="ids-content ids-p-6">
          <p className="ids-body">Laddar...</p>
        </main>
      </div>
    );
  }

  if (!activeOrgId || !activeRoleId) {
    return (
      <div className="ids-mt-6">
        <main className="ids-content ids-p-6">
          {header}
          <IDSAlert type="info" className="ids-mb-4">
            <p>
              Välj organisation och roll för att komma igång. Startsidan visar sedan de
              uppgifter som hör till din roll.
            </p>
          </IDSAlert>
          <IDSButton size="s" onClick={openSelector}>
            Välj organisation och roll
          </IDSButton>
        </main>
      </div>
    );
  }

  const statItems = [];
  const actions = [];
  let intro = "";

  if (isTester) {
    intro =
      "Kör testfall mot din organisations tjänstekontrakt och skicka in testrapporter för granskning.";
    if (stats) {
      statItems.push(
        { value: stats.contracts, label: "Tjänstekontrakt att testa" },
        { value: stats.ongoing, label: "Pågående testkörningar" },
        { value: stats.orgReports, label: "Inskickade rapporter i organisationen" }
      );
    }
    actions.push(
      {
        title: "Testklient",
        description:
          "Välj tjänstekontrakt, konfigurera parametrar och kör testfall – i labbmiljö eller skarpt.",
        actionLabel: "Till testklienten",
        onClick: () => navigate("/testklient"),
      },
      {
        title: "Testrapporter",
        description:
          "Följ organisationens inskickade testrapporter och se var i granskningen de befinner sig.",
        actionLabel: "Visa testrapporter",
        onClick: () => navigate("/testrapporter"),
      }
    );
  } else if (isReviewer) {
    intro = "Granska inskickade testrapporter och verkställ de beslut som väntar i beslutskön.";
    if (stats) {
      statItems.push(
        { value: stats.awaiting, label: "Rapporter att granska" },
        { value: stats.mine, label: "Mina pågående granskningar" },
        { value: stats.decisions, label: "Jira-beslut att verkställa" }
      );
    }
    actions.push(
      {
        title: "Testrapporter att granska",
        description:
          "Öppna granskningskön, ta över en rapport och godkänn eller underkänn den.",
        actionLabel: "Till granskningskön",
        onClick: () => navigate("/testrapporter"),
      },
      {
        title: "Jira beslutskö",
        description: "Verkställ beslut om avvikelser som väntar på hantering.",
        actionLabel: "Till beslutskön",
        onClick: () => navigate("/reviewer/jira-beslut"),
      }
    );
  } else if (isE2ETester) {
    intro =
      "Fyll i och skicka in E2E-underlag, och komplettera dem när granskaren begär förtydligande.";
    if (stats) {
      statItems.push(
        { value: stats.active, label: "Pågående underlag" },
        { value: stats.drafts, label: "Utkast" },
        { value: stats.needsClarification, label: "Behöver förtydligas", highlight: true }
      );
    }
    actions.push(
      {
        title: "E2E Underlag",
        description: "Välj ett formulär och påbörja ett nytt E2E-underlag.",
        actionLabel: "Skapa underlag",
        onClick: () => navigate("/e2e/tester"),
      },
      {
        title: "Inlämnade E2E underlag",
        description: "Se status på dina underlag och komplettera de som behöver förtydligas.",
        actionLabel: "Visa mina underlag",
        onClick: () => navigate("/e2e/tester/submissions"),
      }
    );
  } else if (isE2EReviewer) {
    intro = "Granska inskickade E2E-underlag och begär förtydliganden vid behov.";
    if (stats) {
      statItems.push(
        { value: stats.waiting, label: "Underlag utan granskare" },
        { value: stats.assignedToMe, label: "Tilldelade mig" },
        { value: stats.finished, label: "Slutförda" }
      );
    }
    actions.push({
      title: "E2E Granskarportal",
      description:
        "Ta över underlag, läs svaren och godkänn, avvisa eller begär förtydligande.",
      actionLabel: "Till granskarportalen",
      onClick: () => navigate("/e2e/reviewer"),
    });
  } else if (isAdmin) {
    intro =
      "Administrera organisationer, användare, beställningar och innehållet i testverktyget.";
    if (stats) {
      statItems.push(
        { value: stats.organisations, label: "Organisationer" },
        { value: stats.users, label: "Användare" }
      );
    }
    actions.push(
      {
        title: "Organisationer",
        description: "Skapa och underhåll organisationer och deras uppgifter.",
        actionLabel: "Hantera organisationer",
        onClick: () => navigate("/organisations"),
      },
      {
        title: "Användare",
        description: "Skapa användare och styr vilka roller de har.",
        actionLabel: "Hantera användare",
        onClick: () => navigate("/users"),
      },
      {
        title: "Avdelningar",
        description: "Underhåll avdelningar kopplade till organisationerna.",
        actionLabel: "Hantera avdelningar",
        onClick: () => navigate("/departments"),
      },
      {
        title: "Beställningar",
        description: "Koppla tjänstekontrakt och testare till beställningar.",
        actionLabel: "Hantera beställningar",
        onClick: () => navigate("/serviceorders"),
      },
      {
        title: "Frågor",
        description: "Ändra frågorna som besvaras i testrapporterna.",
        actionLabel: "Ändra frågor",
        onClick: () => navigate("/editquestions"),
      },
      {
        title: "Tjänstekontrakt",
        description: "Underhåll tjänstekontrakt och deras testsviter.",
        actionLabel: "Ändra kontrakt",
        onClick: () => navigate("/editcontracts"),
      },
      {
        title: "TKB-dokument",
        description: "Hantera TKB-dokument och regler.",
        actionLabel: "Hantera TKB",
        onClick: () => navigate("/tkb-admin"),
      }
    );
  }

  return (
    <div className="ids-mt-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto 0" }}>
        {header}
        <p className="ids-body ids-mb-6">{intro}</p>

        {isTester && stats?.needsAction > 0 && (
          <IDSAlert type="attention" className="ids-mb-4">
            <p>
              {stats.needsAction} inskickad(e) rapport(er) har kommit tillbaka från granskningen
              och behöver åtgärdas.
            </p>
          </IDSAlert>
        )}

        {isE2ETester && stats?.needsClarification > 0 && (
          <IDSAlert type="attention" className="ids-mb-4">
            <p>{stats.needsClarification} underlag väntar på ditt förtydligande.</p>
          </IDSAlert>
        )}

        {statsError && (
          <IDSAlert type="attention" className="ids-mb-4">
            <p>{statsError} Du kan fortfarande fortsätta till sidorna nedan.</p>
          </IDSAlert>
        )}

        {statsLoading && <p className="ids-body ids-mb-4">Hämtar din översikt...</p>}

        {statItems.length > 0 && (
          <div style={statGridStyle} className="ids-mb-6">
            {statItems.map((item) => (
              <StatCard key={item.label} {...item} />
            ))}
          </div>
        )}

        <h2 className="ids-heading-m ids-mb-4">Vad vill du göra?</h2>
        <div style={cardGridStyle}>
          {actions.map((action) => (
            <ActionCard key={action.title} {...action} />
          ))}
        </div>
      </main>
    </div>
  );
}

export default Start;
